import React, {useEffect, useRef, useState} from "react";
import {Button} from "primereact/button";
import {ProgressSpinner} from "primereact/progressspinner";
import {useSelector} from "react-redux";
import {useTranslate} from "../system/useTranslate";

const DEFAULT_INTERVAL_MS = 4000;
const MIN_INTERVAL_MS = 2500;
const MAX_INTERVAL_MS = 10000;
const ITEM_TRANSITION_MS = 800;
const PRIME_ICON_PATTERN = /^pi pi-[a-z0-9-]+$/i;

const ROTATOR_ITEM_STYLE = {
    opacity: 0,
    transform: "translateY(15px) scale(0.98)",
    transition: `opacity ${ITEM_TRANSITION_MS}ms cubic-bezier(0.4, 0, 0.2, 1), transform ${ITEM_TRANSITION_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`,
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    pointerEvents: "none",
};

const ROTATOR_ITEM_ACTIVE_STYLE = {
    opacity: 1,
    transform: "translateY(0) scale(1)",
    position: "relative",
    pointerEvents: "auto",
};

const ROTATOR_ITEM_EXIT_STYLE = {
    opacity: 0,
    transform: "translateY(-15px) scale(0.98)",
};

export const normalizeLoginRotatorItems = (items) => {
    if (!Array.isArray(items)) return [];

    return items.reduce((normalizedItems, item) => {
        const text = typeof item === "string" ? item.trim() : item?.text?.trim();
        if (!text) return normalizedItems;

        normalizedItems.push({
            text,
            icon: typeof item?.icon === "string" && PRIME_ICON_PATTERN.test(item.icon) ? item.icon : null,
        });
        return normalizedItems;
    }, []);
};

const normalizeInterval = (value) => {
    const interval = Number(value);
    if (!Number.isFinite(interval)) return DEFAULT_INTERVAL_MS;
    return Math.min(MAX_INTERVAL_MS, Math.max(MIN_INTERVAL_MS, interval));
};

const clearTimeoutRef = (timeoutRef) => {
    if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
    }
};

function LoginTextRotator({className = ""}) {
    const translate = useTranslate();
    const language = useSelector((state) => state.app.config?.language) === "fa" ? "fa" : "en";
    const [items, setItems] = useState([]);
    const [activeIndex, setActiveIndex] = useState(0);
    const [exitingIndex, setExitingIndex] = useState(null);
    const [intervalMs, setIntervalMs] = useState(DEFAULT_INTERVAL_MS);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [attempt, setAttempt] = useState(0);
    const activeIndexRef = useRef(0);
    const exitTimeoutRef = useRef(null);

    useEffect(() => {
        const controller = new AbortController();
        setLoading(true);
        setError(false);
        setActiveIndex(0);
        setExitingIndex(null);
        activeIndexRef.current = 0;
        clearTimeoutRef(exitTimeoutRef);

        fetch(`${process.env.PUBLIC_URL}/content/login-rotator.json`, {signal: controller.signal})
            .then((response) => {
                if (!response.ok) throw new Error("auth-rotator-load-error");
                return response.json();
            })
            .then((payload) => {
                const localizedItems = normalizeLoginRotatorItems(payload?.[language] || payload?.fa || payload?.en);
                if (!localizedItems.length) throw new Error("auth-rotator-load-error");
                setItems(localizedItems);
                setIntervalMs(normalizeInterval(payload?.intervalMs));
                setActiveIndex(0);
                activeIndexRef.current = 0;
            })
            .catch((fetchError) => {
                if (fetchError.name !== "AbortError") {
                    setItems([]);
                    setError(true);
                }
            })
            .finally(() => setLoading(false));

        return () => {
            controller.abort();
            clearTimeoutRef(exitTimeoutRef);
        };
    }, [attempt, language]);

    useEffect(() => {
        if (items.length < 2 || window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches) return undefined;

        const rotate = () => {
            const currentIndex = activeIndexRef.current;
            const nextIndex = (currentIndex + 1) % items.length;

            setExitingIndex(currentIndex);
            setActiveIndex(nextIndex);
            activeIndexRef.current = nextIndex;

            clearTimeoutRef(exitTimeoutRef);
            exitTimeoutRef.current = window.setTimeout(() => {
                setExitingIndex((value) => (value === currentIndex ? null : value));
            }, ITEM_TRANSITION_MS);
        };

        const timer = window.setInterval(rotate, intervalMs);

        return () => {
            window.clearInterval(timer);
            clearTimeoutRef(exitTimeoutRef);
        };
    }, [intervalMs, items.length]);

    if (loading) {
        return (
            <div className={`flex align-items-center justify-content-center gap-2 h-6rem ${className}`}>
                <ProgressSpinner className="w-2rem h-2rem" strokeWidth="5"/>
                <span className="text-xs text-500">{translate("auth-rotator-loading")}</span>
            </div>
        );
    }

    if (error || !items.length) {
        return (
            <div className={`flex flex-column align-items-center justify-content-center gap-2 h-6rem ${className}`}>
                <span className="text-xs text-500">{translate("auth-rotator-load-error")}</span>
                <Button
                    type="button"
                    text
                    icon="pi pi-refresh"
                    className="text-xs gap-2"
                    label={translate("auth-rotator-retry")}
                    onClick={() => setAttempt((value) => value + 1)}
                />
            </div>
        );
    }

    return (
        <div className={`relative flex align-items-center justify-content-center ${className}`} style={{minHeight: "100px"}} aria-live="polite" aria-atomic="true">
            {items.map((item, index) => {
                const isActive = index === activeIndex;
                const isExiting = index === exitingIndex;
                if (!isActive && !isExiting) {
                    return (
                        <div
                            key={`${language}-${index}-${item.text}`}
                            className="flex flex-column align-items-center justify-content-center gap-3 text-center w-full"
                            style={ROTATOR_ITEM_STYLE}
                            aria-hidden="true"
                        >
                            {item.icon && (
                                <span className="w-3rem h-3rem border-circle surface-card shadow-1 flex align-items-center justify-content-center" aria-hidden="true">
                                    <i className={`${item.icon} text-xl text-primary`}/>
                                </span>
                            )}
                            <span className="text-xl xl:text-2xl font-bold text-800 line-height-3">{item.text}</span>
                        </div>
                    );
                }

                return (
                    <div
                        key={`${language}-${index}-${item.text}`}
                        className="flex flex-column align-items-center justify-content-center gap-3 text-center w-full"
                        style={{
                            ...ROTATOR_ITEM_STYLE,
                            ...(isActive ? ROTATOR_ITEM_ACTIVE_STYLE : ROTATOR_ITEM_EXIT_STYLE),
                        }}
                        aria-hidden={!isActive}
                    >
                        {item.icon && (
                            <span className="w-3rem h-3rem border-circle surface-card shadow-1 flex align-items-center justify-content-center" aria-hidden="true">
                                <i className={`${item.icon} text-xl text-primary`}/>
                            </span>
                        )}
                        <span className="text-xl xl:text-2xl font-bold text-800 line-height-3 text-cyan-600">{item.text}</span>
                    </div>
                );
            })}
        </div>
    );
}

export default LoginTextRotator;
